import type { AppNode } from '../types';
import type { Lang } from '../constants';
import { t } from '../i18n';
import { decode } from 'html-entities';
import { Badge } from '@cmsgov/design-system';
import '@docroot/themes/custom/medicare_evo/dist/css/app-card.component.css';

const DEV_BASE = 'https://medicare.ddev.site';

function resolveImageUrl(path: string | null): string | null {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (import.meta.env.DEV) return `${DEV_BASE}${path}`;
  return path;
}

interface AppCardProps {
  app: AppNode;
  lang: Lang;
}

export function AppCard({ app, lang }: AppCardProps) {
  const strings = t(lang);
  const keyFeatures = app.field_app_key_features.map((f) => f.name).join(', ');
  const conditionTerms = app.field_app_condition.map((c) => c.name).join(', ');
  const platforms = app.field_app_platforms.map((p) => p.name).join(', ');
  const iconUrl = resolveImageUrl(app.field_app_icon);
  const price = app.field_app_price || app.field_app_price_type[0].name;

  return (
    <article
      className="m-c-card app-card"
      data-key-features={keyFeatures}
      data-condition-terms={conditionTerms}
      data-track-component="app card"
    >
      <div className="app-card__upper-content">
        <div className="app-card__title-wrapper">
          {iconUrl && (
            <div className="app-card__avatar">
              <img src={iconUrl} alt="" width="80" height="80" loading="lazy" />
            </div>
          )}
          <div className="app-card__title-container">
            <h3 className="ds-text-heading--xl app-card__title">{app.title}</h3>
            {app.field_app_company && (
              <p className="app-card__company">
                {strings.by} {app.field_app_company}
              </p>
            )}
          </div>
        </div>
        <div className="app-card__summary">
          <div className="ds-content ds-content--m-evo">
            {decode(app.body_summary)}
          </div>
        </div>
        <div className="app-card__badges">
          {app.field_app_hte &&
            app.field_app_hte.map((term) => (
              <Badge key={term.tid} hideScreenReaderText variation="info">
                {term.name}
              </Badge>
            ))}
        </div>
      </div>
      <div className="app-card__lower-content">
        <div className="app-card__details">
          <span className="app-card__price">{price}</span>
          <span className="app-card__platforms">{platforms}</span>
        </div>
        <div className="app-card__cta">
          <a className="ds-c-button ds-c-button--solid" href={app.path}>
            {strings.getDetails}{' '}
            <span className="ds-u-visibility--screen-reader">
              for {app.title}
            </span>
          </a>
        </div>
      </div>
    </article>
  );
}
